import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { CoreStore } from '../../../core/state/core.store';
import { WebSocket, WebSocketEventModel } from '../../../core/workflow-loading';
import { IBillOfQuantitiesFilters } from '../../interfaces/bill-of-quantities-filters';
import { BillOfQuantitiesListModel } from '../../models/bill-of-quantities/bill-of-quantities-list';
import { BillOfQuantitiesStatusModel } from '../../models/bill-of-quantities/bill-of-quantities-status';
import { BillOfQuantitiesConfirmedListModel } from '../../models/bill-of-quantities/bill-of-quantitites-confirmed-list';
import { DrawingModel } from '../../models/drawing';
import { PaginationModel } from '../../models/pagination';
import { ResponseModel } from '../../models/response';
import { toHttpParams } from '../../utils/http-params';
import { AuthSession } from '../../../core/auth/auth-session';

@Injectable({
  providedIn: 'root',
})
export class BillOfQuantities {
  private readonly _http = inject(HttpClient);
  private readonly _webSocketService = inject(WebSocket);
  private readonly _config = inject(CoreStore);
  private readonly _authSession = inject(AuthSession);

  private readonly _wsUrl = this._config.wsUrl();
  private readonly _usesLocalMock = /^ws:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/.test(this._wsUrl);

  getBillOfQuantitiesList(
    userId: string,
    filters: IBillOfQuantitiesFilters,
  ): Observable<PaginationModel<BillOfQuantitiesListModel>> {
    const params = toHttpParams({
      page: filters.page, // zero-based
      size: filters.size,
      search: filters.search,
      createdFrom: filters.createdFrom,
      createdTo: filters.createdTo,
      hasDraft: filters.hasDraft,
      step: filters.step,
    });

    return this._http
      .get<ResponseModel<PaginationModel<BillOfQuantitiesListModel>>>(`/list/${userId}`, { params })
      .pipe(map((response) => response.data));
  }

  getBillOfQuantitiesById(id: string): Observable<BillOfQuantitiesConfirmedListModel> {
    return this._http
      .get<ResponseModel<BillOfQuantitiesConfirmedListModel>>(`/confirmed-work-items/${id}`)
      .pipe(map((response) => response.data));
  }

  getBillOfQuantitiesStatus(id: number): Observable<BillOfQuantitiesStatusModel> {
    return this._http
      .get<ResponseModel<BillOfQuantitiesStatusModel>>(`/${id}/status`)
      .pipe(map((response) => response.data));
  }

  getBillOfQuantitiesFile(id: string): Observable<Blob> {
    return this._http.get(`/${id}/files/bill-of-quantities`, {
      responseType: 'blob',
    });
  }

  getBillOfQuantitiesDrawingById(id: number): Observable<Blob> {
    return this._http.get(`/${id}/files/drawing/${id}`, {
      responseType: 'blob',
    });
  }

  getBillOfQuantitiesDrawings(id: number): Observable<DrawingModel[]> {
    return this._http
      .get<ResponseModel<DrawingModel[]>>(`/${id}/files/drawings`)
      .pipe(map((response) => response.data));
  }

  listenWorkflow(operationId: number): Observable<WebSocketEventModel> {
    const token = this._authSession.token;
    const query = this._usesLocalMock && token ? `?access_token=${encodeURIComponent(token)}` : '';
    return this._webSocketService.connect(`${this._wsUrl}/ws/${operationId}${query}`);
  }

  // TODO: chiedere endpoint + interfaccia al BE
  searchProcessing(searchText: string): Observable<any> {
    return this._http.get<any[]>(`/api/processings`, {
      params: {
        search: searchText,
      },
    });
  }

  downloadPdf(id: string): Observable<Blob> {
    return this._http.get(`/confirmed-work-items/${id}/pdf`,{
      responseType: 'blob',
    });
  }
}
